/***
*
*   FEEDBACK TABLE
*   Sortable table of feedback rows with inline pagination controls.
*
**********/

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { convertToLocalTime } from '../../utility/helper';
import { KebabMenu } from '../../components/kebab-menu/KebabMenu';
import { RATING_LABELS, RATING_COLOURS, PAGE_SIZES, COLS, TEXT } from './constants';
import Style from './table.tailwind.js';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function splitDateTime(str) {
  if (!str) return { date: '\u2014', time: '' };
  const s   = String(str);
  const idx = s.indexOf(' ');
  if (idx === -1) return { date: s, time: '' };
  return { date: s.substring(0, idx), time: s.substring(idx + 1) };
}

/** Parse the comment field — may be a JSON blob `{ chatHistory, comment, email }` */
function parseFeedbackComment(raw) {
  if (!raw) return { text: null, chatHistory: null };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        text:        parsed.comment?.trim() || null,
        chatHistory: parsed.chatHistory     || null,
      };
    }
  } catch { /* plain text */ }
  return { text: raw, chatHistory: null };
}

/** Decode HTML entities using the browser's built-in parser */
function decodeHtml(str) {
  const ta = document.createElement('textarea');
  ta.innerHTML = str;
  return ta.value;
}

/**
 * Split the raw chatHistory HTML into { role, markdown } turns.
 *
 * Strategy: split the string on User, Assistant, and Practice Area markers.
 * Practice Area values are captured and attached to the immediately following
 * User turn so they can be rendered as a badge.
 */
function parseChatTurns(html) {
  if (!html) return [];

  // Split on all known role markers, keeping the delimiters in the array
  const parts = html.split(/(<b>(?:User|Assistant|Practice Area)<\/b>(?:<br\s*\/?>)*)/i);

  const turns = [];
  let pendingPracticeArea = null;

  let i = 0;
  while (i < parts.length) {
    const roleMatch = parts[i].match(/^<b>(User|Assistant|Practice Area)<\/b>/i);
    if (!roleMatch) { i++; continue; }

    const role    = roleMatch[1];
    const rawBody = parts[i + 1] || '';

    const cleaned = rawBody
      .replace(/<br\s*\/?>---+<br\s*\/?>/gi, '')
      .replace(/^(?:<br\s*\/?>)+|(?:<br\s*\/?>)+$/g, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .trim();

    const text = decodeHtml(cleaned);

    if (/^practice area$/i.test(role)) {
      pendingPracticeArea = text || null;
    } else if (/^user$/i.test(role)) {
      if (text) turns.push({ role: 'user', markdown: text, practiceArea: pendingPracticeArea });
      pendingPracticeArea = null;
    } else if (/^assistant$/i.test(role)) {
      if (text) turns.push({ role: 'assistant', markdown: text });
      pendingPracticeArea = null;
    }

    i += 2; // advance past the role marker and its body
  }

  return turns;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RatingBadge({ rating }) {
  if (!rating) return <span className={Style.muted}>&mdash;</span>;
  const key           = rating.toLowerCase();
  const { bg, color } = RATING_COLOURS[key] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span className={Style.badge} style={{ background: bg, color }}>
      {RATING_LABELS[key] || rating}
    </span>
  );
}

function SortIcon({ active, dir }) {
  if (!active) return <span className={Style.sortInactive}>&#8597;</span>;
  return <span className={Style.sortActive}>{dir === 'asc' ? '\u2191' : '\u2193'}</span>;
}

function PageBtn({ onClick, disabled, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${Style.pageBtn} ${disabled ? Style.pageBtnOff : Style.pageBtnOn}`}
    >
      {children}
    </button>
  );
}

/** Markdown renderer with consistent prose styling */
function Markdown({ children }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1 className={Style.mdH1}>{children}</h1>,
        h2: ({ children }) => <h2 className={Style.mdH2}>{children}</h2>,
        h3: ({ children }) => <h3 className={Style.mdH3}>{children}</h3>,
        p:  ({ children }) => <p  className={Style.mdP}>{children}</p>,
        ul: ({ children }) => <ul className={Style.mdUl}>{children}</ul>,
        ol: ({ children }) => <ol className={Style.mdOl}>{children}</ol>,
        li: ({ children }) => <li className={Style.mdLi}>{children}</li>,
        strong: ({ children }) => <strong className={Style.mdStrong}>{children}</strong>,
        em:     ({ children }) => <em className={Style.mdEm}>{children}</em>,
        hr: () => <hr className={Style.mdHr} />,
        blockquote: ({ children }) => <blockquote className={Style.mdBlockquote}>{children}</blockquote>,
        code: ({ inline, children }) =>
          inline
            ? <code className={Style.mdCodeInline}>{children}</code>
            : <pre className={Style.mdPre}><code className={Style.mdCode}>{children}</code></pre>,
        table:   ({ children }) => <div className={Style.mdTableWrap}><table className={Style.mdTable}>{children}</table></div>,
        thead:   ({ children }) => <thead className={Style.mdThead}>{children}</thead>,
        tbody:   ({ children }) => <tbody>{children}</tbody>,
        tr:      ({ children }) => <tr className={Style.mdTr}>{children}</tr>,
        th:      ({ children }) => <th className={Style.mdTh}>{children}</th>,
        td:      ({ children }) => <td className={Style.mdTd}>{children}</td>,
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

function ChatHistoryModal({ row, onClose }) {
  const { text, chatHistory } = parseFeedbackComment(row.comment);
  const turns = parseChatTurns(chatHistory);

  return (
    <div className={Style.modalOverlay} style={{ left: 'var(--sidebar-w, 240px)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={Style.modalPanel}>

        {/* Header */}
        <div className={Style.modalHeader}>
          <span className={Style.modalTitle}>Feedback Details</span>
          <button className={Style.modalClose} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className={Style.modalBody}>

          {/* Written comment */}
          {text && (
            <div>
              <p className={Style.modalCommentLbl}>Comment</p>
              <div className={Style.modalComment}>&ldquo;{text}&rdquo;</div>
            </div>
          )}

          {/* Chat turns */}
          {turns.length > 0 && (
            <div className="flex flex-col gap-3">
              {text && <hr className={Style.modalDivider} />}
              <p className={Style.modalCommentLbl}>Conversation</p>

              {turns.map((turn, i) => (
                <div key={i} className={Style.chatTurn}>
                  <div className="flex items-center gap-2">
                    <span className={`${Style.chatRole} ${turn.role === 'user' ? Style.chatRoleUser : Style.chatRoleAsst}`}>
                      {turn.role === 'user' ? 'User' : 'Assistant'}
                    </span>
                    {turn.role === 'user' && turn.practiceArea && (
                      <span className={Style.practiceAreaBadge}>{turn.practiceArea}</span>
                    )}
                  </div>
                  <div className={`${Style.chatBubble} ${turn.role === 'user' ? Style.chatBubbleUser : Style.chatBubbleAsst}`}>
                    {turn.role === 'user'
                      ? <p className="text-base text-gray-800">{turn.markdown}</p>
                      : <Markdown>{turn.markdown}</Markdown>
                    }
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FeedbackTable({
  rows, loading,
  sortKey, sortDir, onSort,
  page, setPage,
  pageSize, setPageSize,
  totalPages,
  onDelete,
}) {
  const [selectedRow, setSelectedRow] = useState(null);

  return (
    <div className={Style.container}>

      {/* Table */}
      <div className={Style.scroll}>
        <table className={Style.table}>
          <thead>
            <tr className={Style.thead}>
              {COLS.map(col => (
                <th
                  key={col.key}
                  onClick={col.sortable ? () => onSort(col.key) : undefined}
                  className={`${Style.th} ${col.sortable ? Style.thSortable : Style.thStatic}`}
                >
                  <span className={Style.thLabel}>
                    {col.label}
                    {col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={COLS.length} className={Style.empty}>{TEXT.tableLoading}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={COLS.length} className={Style.empty}>{TEXT.tableEmpty}</td></tr>
            ) : rows.map(row => {
              const { date, time } = splitDateTime(convertToLocalTime(row.date_created) || '');
              const { text, chatHistory } = parseFeedbackComment(row.comment);
              const displayText = text || row.comment;
              const hasChat     = !!chatHistory;

              return (
                <tr key={row.id} className={Style.row}>

                  <td className={Style.cellDate}>
                    <span className={Style.dateMain}>{date}</span>
                    {time && <span className={Style.dateTime}>{time}</span>}
                  </td>

                  <td className={Style.cellRating}>
                    <RatingBadge rating={row.rating} />
                  </td>

                  <td className={Style.cellErrorType}>
                    {row.error_type || <span className={`${Style.muted} block text-center`}>&mdash;</span>}
                  </td>

                  <td
                    className={Style.cellComment}
                    onClick={() => (displayText || hasChat) && setSelectedRow(row)}
                  >
                    {displayText ? (
                      <>
                        <p className={Style.commentText}>&ldquo;{displayText}&rdquo;</p>
                        {hasChat && (
                          <span className={Style.commentChip}>View conversation &rsaquo;</span>
                        )}
                      </>
                    ) : (
                      <span className={Style.muted}>&mdash;</span>
                    )}
                  </td>

                  <td className={Style.cellEmail}>
                    {row.email || <span className={Style.muted}>&mdash;</span>}
                  </td>

                  <td className={Style.cellActions}>
                    <KebabMenu items={[{ label: TEXT.actionDelete, color: '#dc2626', action: () => onDelete(row) }]} />
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={Style.pagination}>

        <div className={Style.pageSizeGroup}>
          <span>{TEXT.rowsPerPage}</span>
          <select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
            className={Style.pageSizeSelect}
          >
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className={Style.pageCtrlGroup}>
          <span>{TEXT.pageLabel}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={e => {
              const v = Number(e.target.value);
              if (v >= 1 && v <= totalPages) setPage(v);
            }}
            className={Style.pageInput}
          />
          <span>{TEXT.pageOf} {totalPages}</span>
          <PageBtn onClick={() => setPage(1)}          disabled={page === 1}>&#171;</PageBtn>
          <PageBtn onClick={() => setPage(p => Math.max(1, p - 1))}          disabled={page === 1}>&#8249;</PageBtn>
          <PageBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>&#8250;</PageBtn>
          <PageBtn onClick={() => setPage(totalPages)} disabled={page === totalPages}>&#187;</PageBtn>
        </div>

      </div>

      {/* Chat history modal */}
      {selectedRow && (
        <ChatHistoryModal
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
        />
      )}

    </div>
  );
}
